// export default SubscriptionView;
import styles from "./styles.module.scss";
import BadgeIcon from "@mui/icons-material/Badge";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import CircleNotificationsIcon from "@mui/icons-material/CircleNotifications";
import LoyaltyIcon from "@mui/icons-material/Loyalty";
import RoomIcon from "@mui/icons-material/Room";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";

import {
  Avatar,
  Box,
  Button,
  Chip,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Snackbar,
  Typography,
} from "@mui/material";
import Map from "../map/map";
import { api } from "../../services/api";
import { mapNotificationName, mapSubName } from "../../utils/map-names";
import { useMemo, useState } from "react";
import Loader from "../loader/loader";

export interface SubscriptionProps {
  name: string;
  id: string;
  notification: string | string[]; // puede venir como string o array
  subscriptions: string;
  position: string;
  radious?: string;
  handleOnClose?: () => void;
  onDeleteCallback?: () => void;
  // opcionales (mock si no vienen)
  addressText?: string;
  statusText?: string; // "Activa" | "Pausada" etc.
}

const SubscriptionView = ({
  name,
  id,
  notification,
  subscriptions,
  position,
  radious = "0",
  handleOnClose,
  onDeleteCallback,
  addressText,
  statusText = "Activa",
}: SubscriptionProps) => {
  const positionFormated: [number, number] = position
    .split(",")
    .map((item) => parseFloat(item.trim())) as [number, number];

  const [snackBarOpen, setSAnackBarOpen] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const radiusNum = useMemo(() => parseInt(radious || "0", 10), [radious]);

  // Normaliza notificaciones a array y convierte a chips
  const notificationChips = useMemo(() => {
    const raw = Array.isArray(notification)
      ? notification
      : typeof notification === "string"
      ? notification.split(",").map((s) => s.trim())
      : [];
    // mapNotificationName devuelve string; aquí solo mostramos etiquetas base
    const label = (v: string) => {
      const low = v.toLowerCase();
      if (low.includes("whatsapp") || v === "3") return "WhatsApp";
      if (low.includes("push") || v === "2") return "Push";
      if (low.includes("sms") || v === "1") return "SMS";
      if (low.includes("mail") || low.includes("email") || v === "0")
        return "Email";
      // fallback
      return mapNotificationName(v);
    };
    return raw
      .filter(Boolean)
      .map((v, i) => (
        <Chip
          key={`${v}-${i}`}
          size="small"
          label={label(v)}
          color={
            label(v) === "Push"
              ? "success"
              : label(v) === "Email"
              ? "default"
              : "primary"
          }
          variant={label(v) === "Email" ? "outlined" : "filled"}
          sx={{ mr: 1, mb: 1 }}
        />
      ));
  }, [notification]);

  const handleCloseSnackBar = () => setSAnackBarOpen(false);
  const handleOpenSnackBar = () => setSAnackBarOpen(true);

  const snackBar = () => (
    <Snackbar
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      open={snackBarOpen}
      onClose={handleCloseSnackBar}
      message={snackBarMessage}
      autoHideDuration={1500}
      key="snackbar-subscription"
    />
  );

  const deleteUserSubscription = async () => {
    if (
      !window.confirm(
        "¿Eliminar esta suscripción? Esta acción no se puede deshacer."
      )
    ) {
      return;
    }
    setIsLoading(true);
    try {
      await api.subscriptions.deleteById(id);
      setSnackBarMessage("Suscripción eliminada correctamente");
      handleOpenSnackBar();
      setIsLoading(false);
      if (onDeleteCallback) onDeleteCallback();
      if (handleOnClose) handleOnClose();
    } catch {
      setSnackBarMessage("Algo salió mal, inténtalo de nuevo más tarde");
      handleOpenSnackBar();
      setIsLoading(false);
    }
  };

  return (
    <DialogContent
      dividers
      className={`${styles.view_container} flex flex-col text_b`}
    >
      {/* Cerrar */}
      <IconButton
        onClick={handleOnClose ? () => handleOnClose() : () => {}}
        className={`${styles.custom_close_button}`}
        aria-label="close"
      >
        <CloseIcon />
      </IconButton>

      {/* Mapa superior */}
      <Box
        className={`${styles.main_image_container} flex justify-start`}
        sx={{ borderBottom: "1px solid #eee" }}
      >
        <Map
          initialPosition={positionFormated}
          radius={radiusNum}
          height="200px"
        />
      </Box>

      {/* Contenido */}
      <Box
        className={`${styles.main_image_container_content} w-full`}
        sx={{ p: { xs: 2, md: 3 } }}
      >
        <Grid className="w-full" spacing={2}>
          <Grid className="w-full" item xs={12} md={8} lg={7}>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
              Suscripción de colección
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 2 }}
            >
              ID: <strong>{id}</strong>
            </Typography>

            <Paper elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
              <List sx={{ width: "100%" }}>
                {/* Nombre */}
                <ListItem>
                  <ListItemAvatar>
                    <Avatar>
                      <PermIdentityIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography fontWeight={700}>
                        Nombre de ubicación
                      </Typography>
                    }
                    secondary={name}
                  />
                </ListItem>
                <Divider component="li" />

                {/* Dirección (mock si no viene) */}
                <ListItem>
                  <ListItemAvatar>
                    <Avatar>
                      <RoomIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography fontWeight={700}>Dirección</Typography>
                    }
                    secondary={
                      addressText ||
                      `${positionFormated[0].toFixed(
                        4
                      )}, ${positionFormated[1].toFixed(4)}`
                    }
                  />
                </ListItem>
                <Divider component="li" />

                {/* Métodos de notificación */}
                <ListItem sx={{ alignItems: "flex-start" }}>
                  <ListItemAvatar>
                    <Avatar>
                      <CircleNotificationsIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography fontWeight={700}>
                        Métodos de notificación
                      </Typography>
                    }
                    secondary={<Box sx={{ mt: 0.5 }}>{notificationChips}</Box>}
                  />
                </ListItem>
                <Divider component="li" />

                {/* Tipo de alerta */}
                <ListItem>
                  <ListItemAvatar>
                    <Avatar>
                      <LoyaltyIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography fontWeight={700}>Tipo de alerta</Typography>
                    }
                    secondary={mapSubName(subscriptions)}
                  />
                </ListItem>
                <Divider component="li" />

                {/* Radio */}
                <ListItem>
                  <ListItemAvatar>
                    <Avatar>
                      <BadgeIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography fontWeight={700}>
                        Radio de notificación
                      </Typography>
                    }
                    secondary={`${radiusNum} metros`}
                  />
                </ListItem>
                <Divider component="li" />

                {/* Estado */}
                <ListItem>
                  <ListItemAvatar>
                    <Avatar>
                      <TaskAltIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Typography fontWeight={700}>Estado</Typography>}
                    secondary={
                      <Chip
                        label={statusText}
                        color={
                          statusText.toLowerCase() === "activa"
                            ? "success"
                            : "default"
                        }
                        size="small"
                        variant="filled"
                      />
                    }
                  />
                </ListItem>
              </List>
            </Paper>

            {/* Botón eliminar grande */}
            <Box sx={{ mt: 3 }}>
              {isLoading ? (
                <Loader />
              ) : (
                <Button
                  fullWidth
                  type="button"
                  onClick={deleteUserSubscription}
                  color="error"
                  variant="contained"
                  startIcon={<DeleteIcon />}
                  sx={{
                    py: 1.2,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Eliminar suscripción
                </Button>
              )}
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1.5, display: "block" }}
            >
              Al eliminar esta suscripción dejarás de recibir notificaciones
              para esta ubicación.
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {snackBar()}
    </DialogContent>
  );
};

export default SubscriptionView;
